/**
 * One output, and putting a file through it.
 *
 * The three parts of `./fuxi-sounds.ts` that are about the browser's audio
 * rather than about a session. A 复习 session builds one output, fetches a
 * handful of files into it and starts each of them at a moment of its own
 * choosing, and none of those three steps knows what a card is.
 *
 * `docs/fuxi.md` under "The verdict is heard as well as seen" has why this is
 * the Web Audio API rather than an `<audio>` element per file.
 */

/**
 * The output the notes are played through, where this browser has one.
 *
 * Nothing here is worth a broken session, so a browser with no Web Audio and
 * one that refuses to build a context both leave 复习 silent and working. The
 * verdict is in ink, in a mark and in words on the panel either way.
 */
export function openOutput(): AudioContext | undefined {
  try {
    return new AudioContext();
  } catch {
    return undefined;
  }
}

/**
 * Fetch one address and turn it into samples, or into nothing.
 *
 * A file nobody can fetch and a file the browser cannot decode are the same
 * answer here: silence, and a session that carries on. The verdict is in ink,
 * in a mark and in words on the panel either way, and a word the reader cannot
 * hear is one they can still read off the panel.
 */
export async function decoded(
  output: AudioContext,
  address: string,
): Promise<AudioBuffer | undefined> {
  try {
    const answer = await fetch(address);

    return await output.decodeAudioData(await answer.arrayBuffer());
  } catch {
    return undefined;
  }
}

/** What one play needs: the samples, when to start them and how loudly. */
export interface Played {
  /** The output the samples go to. */
  readonly output: AudioContext;

  /** The samples. */
  readonly buffer: AudioBuffer;

  /** When to start, on the output's own clock. */
  readonly at: number;

  /** How loudly, from 0 to 1. */
  readonly gain: number;
}

/**
 * Start one buffer through the output.
 *
 * A source node plays once and is thrown away. That is the shape the API
 * takes, and it is what lets a reader answering quickly get the next card's
 * note over the tail of the last one. At half a second that is what two
 * overlapping cards should sound like.
 */
export function play(properties: Played): AudioBufferSourceNode {
  const source = properties.output.createBufferSource();
  const gain = properties.output.createGain();

  source.buffer = properties.buffer;
  gain.gain.value = properties.gain;
  source.connect(gain).connect(properties.output.destination);
  source.start(properties.at);

  return source;
}

/**
 * The events a browser lets an output start running from.
 *
 * **Not `pointerdown`.** The HTML standard grants user activation on a
 * pointer's `pointerdown` only where the pointer is a mouse, and on a finger's
 * `pointerup` and `touchend`. A context resumed from a touch going down stays
 * suspended, which is what left a session silent on every browser on iOS while
 * a laptop heard it. `touchend` beside `pointerup` covers a WebKit that counts
 * the one and not the other. A mouse's `pointerup` still falls inside the
 * activation its own `pointerdown` granted.
 */
const gestures = ["pointerup", "touchend", "keydown"] as const;

/**
 * Let the browser play these without a gesture of its own.
 *
 * A marking arrives from a request rather than from the press that sent it, so
 * by the time there is something to play the reader's gesture is over. A
 * context built before any gesture starts suspended, and a browser resumes one
 * only from inside a listener on an event that grants activation.
 *
 * **Every gesture until it runs, and again whenever it stops.** A refused
 * resume leaves the context suspended, and iOS suspends a running one when a
 * call or another app takes the audio. Listening for one gesture and no more
 * left both silent for the rest of the session. A gesture over a running
 * context reads its state and does nothing else.
 */
export function wakeOnGestures(page: Document, output: AudioContext): void {
  const wake = (): void => {
    if (output.state !== "running") {
      void output.resume().catch(() => undefined);
    }
  };

  for (const gesture of gestures) {
    page.addEventListener(gesture, wake);
  }
}
